import { effectiveRange, effectiveValues, isLocked, type LayerBulk, type ParamBulkSpec } from '../core/params/bulk';
import type { Param, ParamValue } from '../core/params/types';
import { BACKGROUND_PARAMS } from '../core/background';
import { POST_EFFECTS } from '../core/postfx/chain';
import { buildTextSchema } from '../core/text/textParams';
import { getTechnique } from '../techniques/registry';
import { TEXT_TECHNIQUE_ID } from '../store/layer';
import type { StudioDocument } from '../store/studioStore';

/**
 * A single axis of variation.
 *
 * Rather than a second, parallel range UI, the batch engine reuses the per-parameter
 * exploration ranges from the studio: any unlocked parameter *is* a dimension, bounded
 * by exactly the range the user already sees on its slider. That keeps one concept
 * instead of two, and means narrowing a range in the studio immediately narrows the
 * batch.
 */
export interface Dimension {
  id: string;
  label: string;
  group: string;
  scope: 'text' | 'layer' | 'background' | 'post';
  /** Layer id for `layer` scope, effect id for `post` scope. */
  ownerId?: string;
  param: Param;
  spec?: ParamBulkSpec;
}

export interface DimensionSources {
  /** Only these layers contribute dimensions. */
  layerIds: Set<string>;
  includeText: boolean;
  includeBackground: boolean;
  includePost: boolean;
}

function isVariable(param: Param, bulk: LayerBulk | undefined): boolean {
  if (param.neverRandomise) return false;
  if (isLocked(bulk, param.key)) return false;
  // Only kinds with a well-defined value space can be swept.
  return (
    param.kind === 'float' ||
    param.kind === 'int' ||
    param.kind === 'bool' ||
    param.kind === 'enum' ||
    (param.kind === 'text' && (param.bulkValues?.length ?? 0) > 0)
  );
}

export function collectDimensions(doc: StudioDocument, sources: DimensionSources): Dimension[] {
  const out: Dimension[] = [];

  if (sources.includeText) {
    for (const param of buildTextSchema(null)) {
      if (!isVariable(param, doc.textBulk)) continue;
      out.push({
        id: `text:${param.key}`,
        label: param.label,
        group: 'Text',
        scope: 'text',
        param,
        spec: doc.textBulk[param.key],
      });
    }
  }

  for (const layer of doc.layers) {
    if (layer.techniqueId === TEXT_TECHNIQUE_ID) continue;
    if (!sources.layerIds.has(layer.id)) continue;
    const technique = getTechnique(layer.techniqueId);
    if (!technique) continue;
    for (const param of technique.params) {
      if (!isVariable(param, layer.bulk)) continue;
      out.push({
        id: `layer:${layer.id}:${param.key}`,
        label: `${technique.name}: ${param.label}`,
        group: technique.name,
        scope: 'layer',
        ownerId: layer.id,
        param,
        spec: layer.bulk[param.key],
      });
    }
  }

  if (sources.includeBackground) {
    for (const param of BACKGROUND_PARAMS) {
      if (!isVariable(param, undefined)) continue;
      out.push({
        id: `background:${param.key}`,
        label: `Hintergrund: ${param.label}`,
        group: 'Hintergrund',
        scope: 'background',
        param,
      });
    }
  }

  if (sources.includePost) {
    for (const effect of POST_EFFECTS) {
      if (!doc.postFx[effect.id]?.enabled) continue;
      for (const param of effect.params) {
        if (!isVariable(param, undefined)) continue;
        out.push({
          id: `post:${effect.id}:${param.key}`,
          label: `${effect.name}: ${param.label}`,
          group: `Post-FX · ${effect.name}`,
          scope: 'post',
          ownerId: effect.id,
          param,
        });
      }
    }
  }

  return out;
}

/**
 * Maps a uniform sample in [0, 1) onto a parameter's value space.
 *
 * Every strategy — random, grid, Latin hypercube — produces numbers in the unit
 * interval and hands them here, so they all honour ranges, steps and allowed value
 * sets identically. The strategies differ in *how they place* the samples, never in
 * what a sample means.
 */
export function valueFromUnit(dim: Dimension, u: number): ParamValue {
  const { param, spec } = dim;
  const t = Math.min(0.999999, Math.max(0, u));

  switch (param.kind) {
    case 'float':
    case 'int': {
      const [lo, hi] = effectiveRange(param, spec);
      const raw = lo + (hi - lo) * t;
      const snapped = Math.round(raw / param.step) * param.step;
      const clamped = Math.min(hi, Math.max(lo, snapped));
      return param.kind === 'int' ? Math.round(clamped) : Number(clamped.toFixed(6));
    }
    case 'bool':
      return t < (param.bulkProbability ?? 0.5);
    case 'enum':
    case 'text': {
      const values = effectiveValues(param, spec);
      if (!values.length) return param.default;
      return values[Math.min(values.length - 1, Math.floor(t * values.length))];
    }
    default:
      return param.default as ParamValue;
  }
}

/** Writes a sampled value into the right place in a document. */
export function applyDimension(doc: StudioDocument, dim: Dimension, value: ParamValue): void {
  switch (dim.scope) {
    case 'text':
      doc.textValues[dim.param.key] = value;
      break;
    case 'layer': {
      const layer = doc.layers.find((l) => l.id === dim.ownerId);
      if (layer) layer.values[dim.param.key] = value;
      break;
    }
    case 'background':
      doc.background[dim.param.key] = value;
      break;
    case 'post': {
      const entry = dim.ownerId ? doc.postFx[dim.ownerId] : undefined;
      if (entry) entry.values[dim.param.key] = value;
      break;
    }
  }
}

export function readDimension(doc: StudioDocument, dim: Dimension): ParamValue | undefined {
  switch (dim.scope) {
    case 'text':
      return doc.textValues[dim.param.key];
    case 'layer':
      return doc.layers.find((l) => l.id === dim.ownerId)?.values[dim.param.key];
    case 'background':
      return doc.background[dim.param.key];
    case 'post':
      return dim.ownerId ? doc.postFx[dim.ownerId]?.values[dim.param.key] : undefined;
  }
}
